import React, { useEffect, useState } from 'react';
import { useTheme, Box, Typography, Button, CircularProgress, Chip } from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PixIcon from '@mui/icons-material/Pix';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

function statusPill(status) {
  if (status === 'RECEIVED' || status === 'CONFIRMED') return <Chip label="PAGO" size="small" sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 700, fontSize: 11 }} />;
  if (status === 'PENDING') return <Chip label="AGUARDANDO" size="small" sx={{ bgcolor: '#fef3c7', color: '#b45309', fontWeight: 700, fontSize: 11 }} />;
  if (status === 'CANCELED') return <Chip label="CANCELADO" size="small" sx={{ bgcolor: '#fee2e2', color: '#b91c1c', fontWeight: 700, fontSize: 11 }} />;
  if (status === 'OVERDUE') return <Chip label="VENCIDO" size="small" sx={{ bgcolor: '#fee2e2', color: '#b91c1c', fontWeight: 700, fontSize: 11 }} />;
  return <Chip label={status} size="small" />;
}

function formatarData(data) {
  if (!data) return '—';
  return new Date(data).toLocaleDateString('pt-BR');
}

export default function PlanoProDetalhes() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  const clinicaId = user?.clinicaId || user?.clinica_id || user?.clinica?.id;
  const customerId = user?.clinica?.asaasCustomerId || user?.clinica?.asaas_customer_id || user?.asaasCustomerId;
  const metodoPagamento = user?.clinica?.metodoPagamento || user?.clinica?.metodo_pagamento;

  const [assinatura, setAssinatura] = useState(null);
  const [cobrancas, setCobrancas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelando, setCancelando] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    async function carregar() {
      setLoading(true);
      try {
        const [assinaturaRes, cobrancasRes] = await Promise.all([
          api.get(`/asaas/assinatura?clinicaId=${clinicaId}`).catch(() => null),
          customerId ? api.get(`/asaas/cobrancas?customer=${customerId}`).catch(() => null) : Promise.resolve(null)
        ]);
        setAssinatura(assinaturaRes);
        const todasCobrancas = cobrancasRes?.data || (Array.isArray(cobrancasRes) ? cobrancasRes : []);
        const lista = Array.isArray(todasCobrancas) ? todasCobrancas : [];
        setCobrancas(lista);
        setErro('');
      } catch {
        // erros individuais já tratados acima
      }
      setLoading(false);
    }
    if (clinicaId) carregar();
  }, [clinicaId, customerId]);

  const handleCancelar = async () => {
    if (!window.confirm('Tem certeza que deseja cancelar sua assinatura? Seu plano voltará para FREE.')) return;
    setCancelando(true);
    try {
      await api.delete('/asaas/cancelar-assinatura', { data: { clinicaId } });
      const userAtualizado = { ...user };
      if (userAtualizado.clinica) userAtualizado.clinica.plano = 'FREE';
      else userAtualizado.plano = 'FREE';
      setUser(userAtualizado);
      localStorage.setItem('user', JSON.stringify(userAtualizado));
      navigate('/usuario/assinatura');
    } catch {
      alert('Erro ao cancelar assinatura. Tente novamente.');
    }
    setCancelando(false);
  };

  const bgPaper = theme.palette.background.paper;
  const bgDefault = theme.palette.background.default;
  const border = isDark ? '#334155' : '#e2e8f0';

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}>
      <CircularProgress />
    </Box>
  );

  const proximaCobranca = assinatura?.nextDueDate;
  const statusAssinatura = assinatura?.status;

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', py: 4, px: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 4 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Configurações da Assinatura</Typography>
          <Typography color="text.secondary" mt={0.5}>Plano PRO — Gerencie Odonto</Typography>
        </Box>
        <Chip
          label={statusAssinatura === 'ACTIVE' ? 'ATIVA' : statusAssinatura || 'PRO'}
          sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 700 }}
        />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
        {/* Coluna esquerda */}
        <Box>
          {/* Método de pagamento */}
          <Box sx={{ bgcolor: bgPaper, border: `1px solid ${border}`, borderRadius: 3, mb: 3, overflow: 'hidden' }}>
            <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${border}` }}>
              <Typography fontWeight={600}>Método de Pagamento</Typography>
            </Box>
            <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ width: 48, height: 36, bgcolor: metodoPagamento === 'PIX' ? '#00bfa5' : '#1e293b', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {metodoPagamento === 'PIX'
                  ? <PixIcon sx={{ color: 'white', fontSize: 22 }} />
                  : <CreditCardIcon sx={{ color: 'white', fontSize: 20 }} />}
              </Box>
              <Box>
                <Typography fontWeight={600} fontSize={15}>
                  {metodoPagamento === 'PIX' ? 'PIX Recorrente' : 'Cartão de Crédito'}
                </Typography>
                <Typography color="text.secondary" fontSize={13}>
                  {metodoPagamento === 'PIX'
                    ? 'QR Code gerado automaticamente todo mês'
                    : 'Cobrança automática no vencimento'}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                sx={{ ml: 'auto' }}
                onClick={() => navigate('/usuario/pagamentopro')}
              >
                Trocar método
              </Button>
            </Box>
          </Box>

          {/* Histórico */}
          <Box sx={{ bgcolor: bgPaper, border: `1px solid ${border}`, borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${border}` }}>
              <Typography fontWeight={600}>Histórico de Faturas</Typography>
            </Box>
            {erro && <Typography color="error" sx={{ p: 3 }}>{erro}</Typography>}
            {!erro && cobrancas.length === 0 && (
              <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>Nenhuma fatura encontrada.</Typography>
            )}
            {cobrancas.length > 0 && (
              <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                <Box component="thead">
                  <Box component="tr" sx={{ bgcolor: bgDefault }}>
                    {['Data', 'Valor', 'Status', 'Ação'].map(h => (
                      <Box component="th" key={h} sx={{ px: 3, py: 1.5, textAlign: 'left', fontSize: 11, color: 'text.secondary', textTransform: 'uppercase', fontWeight: 600 }}>{h}</Box>
                    ))}
                  </Box>
                </Box>
                <Box component="tbody">
                  {cobrancas.map(c => (
                    <Box component="tr" key={c.id} sx={{ borderTop: `1px solid ${border}` }}>
                      <Box component="td" sx={{ px: 3, py: 2, fontSize: 14 }}>{formatarData(c.dueDate)}</Box>
                      <Box component="td" sx={{ px: 3, py: 2, fontSize: 14 }}>{Number(c.value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</Box>
                      <Box component="td" sx={{ px: 3, py: 2 }}>{statusPill(c.status)}</Box>
                      <Box component="td" sx={{ px: 3, py: 2 }}>
                        {(c.status === 'PENDING' || c.status === 'OVERDUE') && c.invoiceUrl && (
                          <Button
                            size="small"
                            variant="contained"
                            endIcon={<OpenInNewIcon fontSize="small" />}
                            href={c.invoiceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ bgcolor: '#ea580c', '&:hover': { bgcolor: '#c2410c' }, fontWeight: 600, fontSize: 12 }}
                          >
                            Pagar agora
                          </Button>
                        )}
                        {c.status === 'RECEIVED' || c.status === 'CONFIRMED' ? (
                          <Typography fontSize={13} color="text.secondary">—</Typography>
                        ) : null}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* Coluna direita */}
        <Box>
          <Box sx={{ bgcolor: '#2563eb', color: 'white', borderRadius: 3, p: 3, mb: 3 }}>
            <Typography fontSize={11} sx={{ opacity: 0.8, textTransform: 'uppercase', mb: 1 }}>Próxima Cobrança</Typography>
            <Typography fontSize={28} fontWeight={700}>{formatarData(proximaCobranca)}</Typography>
            <Typography fontSize={13} sx={{ opacity: 0.9, mt: 1 }}>R$ 129,90/mês — renovação automática</Typography>
          </Box>

          <Box sx={{ bgcolor: bgPaper, border: `1px solid ${border}`, borderRadius: 3, p: 3 }}>
            <Typography fontWeight={600} fontSize={14} mb={2}>Resumo do Plano PRO</Typography>
            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, fontSize: 13, color: 'text.secondary' }}>
              {['Pacientes Ilimitados', 'Notas Fiscais Ilimitadas', 'Fluxo de Caixa Ilimitado', 'Agendamentos Ilimitados', 'Confirmação via WhatsApp', 'Suporte Prioritário'].map(item => (
                <Box component="li" key={item} sx={{ mb: 1 }}>✓ {item}</Box>
              ))}
            </Box>
            <Box sx={{ borderTop: '2px dashed #fee2e2', pt: 2.5, mt: 2.5 }}>
              <Button
                color="error"
                variant="text"
                size="small"
                disabled={cancelando}
                onClick={handleCancelar}
                sx={{ fontSize: 13, textDecoration: 'underline', p: 0 }}
              >
                {cancelando ? 'Cancelando...' : 'Cancelar assinatura'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
